(() => {
  /** @typedef {{ destroy: (deleteInstance?: boolean, cleanStyles?: boolean) => void, params?: Record<string, unknown> }} SwiperInstance */
  /** @typedef {new (container: Element, options: Record<string, unknown>) => SwiperInstance} SwiperConstructor */
  /** @typedef {Window & { Swiper?: SwiperConstructor }} SwiperWindow */
  /** @type {SwiperWindow} */
  const win = window;
  const SECTION_SELECTOR = "[data-related-collections-plp]";
  const SWIPER_SELECTOR = ".related-collections-plp__swiper";
  const SWIPER_RETRY_DELAY_MS = 120;
  const SWIPER_RETRY_LIMIT = 30;
  let swiperRetryCount = 0;
  /** @type {number | null} */
  let swiperRetryTimer = null;

  /** @param {HTMLElement} sectionEl */
  const initSection = (sectionEl) => {
    if (sectionEl.dataset.relatedCollectionsInit === "true") return;
    const swiperContainer = sectionEl.querySelector(SWIPER_SELECTOR);
    if (!(swiperContainer instanceof HTMLElement)) return;
    if (typeof win.Swiper !== "function") return false;

    const slideCount = swiperContainer.querySelectorAll(".swiper-slide").length;
    if (slideCount <= 1) return true;

    sectionEl.dataset.relatedCollectionsInit = "true";
    /** @type {SwiperInstance | null} */
    let swiperInstance = new win.Swiper(swiperContainer, {
      slidesPerView: "auto",
      spaceBetween: 12,
      freeMode: true,
      watchOverflow: true,
    });

    const onSectionUnload = () => {
      swiperInstance?.destroy(true, true);
      swiperInstance = null;
      delete sectionEl.dataset.relatedCollectionsInit;
      sectionEl.removeEventListener("shopify:section:unload", onSectionUnload);
    };

    sectionEl.addEventListener("shopify:section:unload", onSectionUnload);
    return true;
  };

  /** @param {ParentNode} [root=document] */
  const initAll = (root = document) => {
    let hasPendingSwiper = false;
    const sections = root.querySelectorAll(SECTION_SELECTOR);
    for (let i = 0; i < sections.length; i += 1) {
      const sectionEl = sections[i];
      if (!(sectionEl instanceof HTMLElement)) continue;
      const isInitialized = initSection(sectionEl);
      if (!isInitialized && sectionEl.dataset.relatedCollectionsInit !== "true") {
        hasPendingSwiper = true;
      }
    }
    return hasPendingSwiper;
  };

  const scheduleRetry = () => {
    if (typeof win.Swiper === "function") {
      swiperRetryCount = 0;
      if (swiperRetryTimer !== null) {
        clearTimeout(swiperRetryTimer);
        swiperRetryTimer = null;
      }
      return;
    }

    if (swiperRetryTimer !== null) return;
    if (swiperRetryCount >= SWIPER_RETRY_LIMIT) return;

    swiperRetryTimer = window.setTimeout(() => {
      swiperRetryTimer = null;
      swiperRetryCount += 1;
      const hasPendingSwiper = initAll(document);
      if (hasPendingSwiper) scheduleRetry();
    }, SWIPER_RETRY_DELAY_MS);
  };

  if (document.readyState === "loading") {
    document.addEventListener(
      "DOMContentLoaded",
      () => {
        const hasPendingSwiper = initAll();
        if (hasPendingSwiper) scheduleRetry();
      },
      { once: true },
    );
  } else {
    const hasPendingSwiper = initAll();
    if (hasPendingSwiper) scheduleRetry();
  }

  document.addEventListener("shopify:section:load", (event) => {
    const { target } = event;
    let hasPendingSwiper = false;
    if (target instanceof Element || target instanceof Document) {
      hasPendingSwiper = initAll(target);
    } else {
      hasPendingSwiper = initAll(document);
    }
    if (hasPendingSwiper) scheduleRetry();
  });
})();
